import {useEffect, useMemo, useState} from 'react'
import {Modal, Spinner, Button} from 'react-bootstrap'
import {FiDownload} from 'react-icons/fi'
import styles from './AnexoViewer.module.css'
import buttonStyles from '../form/Button.module.css'

const tipos = {pdf: 'application/pdf', png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', gif: 'image/gif', webp: 'image/webp', avif: 'image/avif', bmp: 'image/bmp', svg: 'image/svg+xml', xml: 'application/xml', docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'}
const extensoesPorTipo = Object.entries(tipos).reduce((resultado, [extensao, tipo]) => ({...resultado, [tipo]: extensao === 'jpeg' ? 'jpg' : extensao}), {})

function tipoPeloNome(nome = '') {
    const extensao = String(nome).match(/\.([a-z0-9]+)(?:[?#].*)?$/i)?.[1]?.toLowerCase() || ''
    return tipos[extensao] || ''
}

function extensaoPeloContentDisposition(valor = '') {
    const nome = valor.match(/filename\*?=(?:UTF-8''|["']?)([^"';]+)/i)?.[1]
    if (!nome) return ''
    try {
        return decodeURIComponent(nome).match(/\.([a-z0-9]+)$/i)?.[1]?.toLowerCase() || ''
    } catch (_) {
        return nome.match(/\.([a-z0-9]+)$/i)?.[1]?.toLowerCase() || ''
    }
}

async function detectarPelaAssinatura(blob) {
    const bytes = new Uint8Array(await blob.slice(0, 16).arrayBuffer())
    const texto = new TextDecoder().decode(bytes)
    if (texto.startsWith('%PDF')) return 'pdf'
    if (bytes[0] === 0x89 && texto.slice(1, 4) === 'PNG') return 'png'
    if (bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) return 'jpg'
    if (texto.startsWith('GIF8')) return 'gif'
    if (texto.startsWith('RIFF') && texto.slice(8, 12) === 'WEBP') return 'webp'
    if (texto.trimStart().startsWith('<?xml') || texto.trimStart().startsWith('<')) return 'xml'
    if (texto.startsWith('PK')) return 'docx'
    return ''
}

function getNome(anexo) {
    return anexo?.TX_NOME || anexo?.nome || ''
}

function getId(anexo) {
    return anexo?.ID || anexo?.id || ''
}

function getUrl(anexo, baseUrl) {
    if (anexo?.url) return anexo.url
    const nome = getNome(anexo)
    if (!nome) return ''
    return `${baseUrl.replace(/\/$/, '')}/${encodeURIComponent(nome)}`
}

function AnexoViewer({anexos = [], baseUrl = '', selectedId = null, url = '', nome = '', id = '', onTipoDetectado, onClose}) {
    const lista = useMemo(() => {
        if (Array.isArray(anexos) && anexos.length > 0) return anexos
        if (nome || id || url) {
            return [{TX_NOME: nome, ID: id, url}]
        }
        return []
    }, [anexos, url, nome, id])

    const indiceInicial = Math.max(0, lista.findIndex((anexo) => getId(anexo) === selectedId))
    const [indice, setIndice] = useState(indiceInicial)
    const [arquivo, setArquivo] = useState(null)
    const [erro, setErro] = useState(false)

    const anexoAtual = useMemo(() => lista[indice] || lista[0] || {}, [lista, indice])
    const nomeAtual = getNome(anexoAtual)
    const idAtual = getId(anexoAtual)

    useEffect(() => {
        const controller = new AbortController()
        let objectUrl = null
        setArquivo(null)
        setErro(false)

        async function carregar() {
            try {
                const currentUrl = getUrl(anexoAtual, baseUrl)
                const response = await fetch(currentUrl, {signal: controller.signal})
                if (!response.ok) throw new Error('Falha ao buscar anexo')

                const blob = await response.blob()
                if (controller.signal.aborted) return

                const tipoDoNome = tipoPeloNome(nomeAtual)
                const extensaoDoHeader = extensaoPeloContentDisposition(response.headers?.get('Content-Disposition') || '')
                const tipoResposta = (blob.type && blob.type !== 'application/octet-stream')
                    ? blob.type.split(';')[0].toLowerCase() : ''
                const extensao = extensaoDoHeader || extensoesPorTipo[tipoResposta] || await detectarPelaAssinatura(blob) || extensoesPorTipo[tipoDoNome] || ''
                const tipo = tipos[extensao] || tipoResposta || tipoDoNome || 'application/octet-stream'

                const blobFinal = (!blob.type || blob.type === 'application/octet-stream')
                    ? new Blob([blob], {type: tipo})
                    : blob

                objectUrl = URL.createObjectURL(blobFinal)
                setArquivo({url: objectUrl, tipo, extensao})
                if (extensao && onTipoDetectado) onTipoDetectado(idAtual, extensao)
            } catch (error) {
                if (!controller.signal.aborted) setErro(true)
            }
        }

        carregar()

        return () => {
            controller.abort()
            if (objectUrl) URL.revokeObjectURL(objectUrl)
        }
    }, [anexoAtual, baseUrl, nomeAtual, idAtual, onTipoDetectado])

    const anterior = () => {
        if (lista.length <= 1) return
        setIndice((valor) => (valor === 0 ? lista.length - 1 : valor - 1))
    }

    const proximo = () => {
        if (lista.length <= 1) return
        setIndice((valor) => (valor + 1) % lista.length)
    }

    return (
        <Modal show onHide={onClose} size="xl" centered aria-labelledby="anexo-viewer-title">
            <Modal.Header closeButton>
                <Modal.Title id="anexo-viewer-title">Anexo {idAtual || id}</Modal.Title>
            </Modal.Header>
            <Modal.Body className={styles.conteudo}>
                {erro ? <p role="alert">Não foi possível carregar o anexo. Feche e tente novamente.</p>
                    : !arquivo ? <div role="status"><Spinner animation="border" size="sm" /> Carregando anexo...</div>
                    : <div className={styles.carrosselWrap}>
                        {lista.length > 1 && (
                            <Button className={styles.seta} variant="outline-secondary" onClick={anterior} aria-label="Anexo anterior">
                                ‹
                            </Button>
                        )}
                        <div className={styles.carrosselImagemWrap}>
                            {arquivo.tipo.startsWith('image/') ? (
                                <img className={styles.imagem} src={arquivo.url} alt={`Anexo ${idAtual || id}`} onError={() => setErro(true)} />
                            ) : arquivo.tipo === 'application/pdf' ? (
                                <iframe className={styles.webview} src={arquivo.url} title={`Anexo ${idAtual || id}`} />
                            ) : (
                                <p>Este formato não possui visualização. Baixe o arquivo para abri-lo.</p>
                            )}
                        </div>
                        {lista.length > 1 && (
                            <Button className={styles.seta} variant="outline-secondary" onClick={proximo} aria-label="Próximo anexo">
                                ›
                            </Button>
                        )}
                    </div>}
            </Modal.Body>
            {arquivo && (
                <Modal.Footer className={styles.footer}>
                    <span className={styles.indicador}>{`${indice + 1}/${lista.length}`}</span>
                    <a className={`${buttonStyles.btn} ${styles.download}`} href={arquivo.url} download={nomeAtual.includes('.') ? nomeAtual : `${nomeAtual}.${arquivo.extensao}`}>
                        <FiDownload aria-hidden="true" />
                         &nbsp;Baixar anexo
                    </a>
                </Modal.Footer>
            )}
        </Modal>
    )
}

export default AnexoViewer
